/**
 * Configuration provided by the host page via window.currentsConfig
 */
export interface CurrentsConfig {
  /** Required: UUID of the club in the Currents system */
  clubId: string
  /** Environment: 'production' | 'staging' — defaults to 'production' */
  env?: 'production' | 'staging'
  /** Skip the verification step (go straight from login to date) */
  skipVerification?: boolean
  /** Require phone number on login/player forms */
  requirePhone?: boolean
  /** Display mode: 'floating' (default popup) or 'inline' (embedded in page) */
  mode?: 'floating' | 'inline'
  /** CSS selector for the container element when mode is 'inline' */
  container?: string
}

/**
 * Brand profile fetched from the Currents API.
 * Currents controls all visual aspects — the club admin only provides their clubId.
 */
export interface BrandProfile {
  clubName: string
  skin: 'default' | 'dark'
  theme: 'light' | 'dark'
  colors: {
    primary: string
    accent: string
    background: string
    text: string
    buttonText: string
    error: string
  }
  typography: {
    fontFamily: string
    headingFontFamily: string
    borderRadius: string
  }
  assets: {
    logoUrl: string
    logoDarkUrl: string
  }
  button: {
    text: string
    position: 'bottom-right' | 'bottom-left' | 'bottom-center'
    style: 'pill' | 'rounded' | 'square'
    icon: boolean
  }
  customCSS: string
  features: {
    showPoweredBy: boolean
    mobileBehaviour: 'fullscreen' | 'drawer' | 'disabled'
    currency: string
    locale: string
  }
}

/**
 * Default brand profile used when the API is unreachable and no cache exists.
 */
export const DEFAULT_BRAND_PROFILE: BrandProfile = {
  clubName: 'Golf Club',
  skin: 'default',
  theme: 'light',
  colors: {
    primary: '#1e3a5f',
    accent: '#c8a960',
    background: '#f5f3ef',
    text: '#1a1a2e',
    buttonText: '#ffffff',
    error: '#f85149',
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    headingFontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    borderRadius: '8px',
  },
  assets: {
    logoUrl: '',
    logoDarkUrl: '',
  },
  button: {
    text: 'Book a Tee Time',
    position: 'bottom-right',
    style: 'pill',
    icon: true,
  },
  customCSS: '',
  features: {
    showPoweredBy: true,
    mobileBehaviour: 'fullscreen',
    currency: 'AUD',
    locale: 'en-AU',
  },
}

// ─── Booking Domain Types ────────────────────────────────────────────────────

export type BookingAccess = 'PUBLIC' | 'RECIPROCAL' | 'MEMBERS_ONLY' | 'PRIVATE'

export interface TimeSlot {
  id: string
  time: string
  available: boolean
  perAmount: number
  domesticPrice: number
  internationalPrice: number
  maxPlayers: number
  remainingSlots: number
  courseId?: string
  courseName?: string
  clubNote?: string
  reciprocalPricingEnabled?: boolean
  homeClubRequired?: boolean
  bookingAccess?: BookingAccess
}

export interface PlayerInfo {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  title: string
  handicap?: string
  homeClubId?: string
  homeClub: {
    name: string
    country: string
    state?: string
    city?: string
    address: string
    zip?: string
    website?: string
    phone?: string
    email?: string
  }
  isPrimary: boolean
  bookingOptions: {
    caddie: boolean
    clubHire: boolean
    clubHireShaft?: 'steel' | 'graphite'
    clubHireHand?: 'right' | 'left'
    cart: boolean
  }
  priceCents?: number
  isReciprocalMember?: boolean
  priceLabel?: string
}

declare global {
  interface Window {
    currentsConfig?: Partial<CurrentsConfig>
    dataLayer?: Array<Record<string, unknown>>
  }
}
